import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { format, parseISO, subDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { FileText, Loader2, Calendar, User, MapPin, Monitor, Clock, Users, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Helper to format time in EST
const formatTimeEST = (isoString) => {
  const date = parseISO(isoString);
  const estDate = toZonedTime(date, "America/New_York");
  return format(estDate, "h:mm a") + " EST";
};

export default function TeamLogsPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedDate, setSelectedDate] = useState(format(subDays(new Date(), 1), "yyyy-MM-dd"));
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");

  // Get last 14 days for date selection
  const getDateOptions = () => {
    const dates = [];
    for (let i = 0; i <= 14; i++) {
      const date = subDays(new Date(), i);
      dates.push({
        value: format(date, "yyyy-MM-dd"),
        label: i === 0 ? `Today (${format(date, "MMM d")})` : i === 1 ? `Yesterday (${format(date, "MMM d")})` : format(date, "EEEE, MMM d"),
      });
    }
    return dates;
  };

  const dateOptions = getDateOptions();

  useEffect(() => {
    if (user?.team_ids && user.team_ids.length > 0) {
      fetchTeams();
    }
  }, [user]);

  useEffect(() => {
    fetchTeamMembers();
  }, [selectedTeam, user]);

  useEffect(() => {
    fetchLogs();
  }, [selectedDate, selectedTeam, selectedUser]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      // Filter to only show user's teams
      const userTeams = response.data.filter(t => user.team_ids.includes(t.id));
      setTeams(userTeams);
    } catch (error) {
      console.error("Failed to fetch teams");
    }
  };

  const fetchTeamMembers = async () => {
    if (!user?.team_ids || user.team_ids.length === 0) return;
    
    try {
      // Get members from selected team or all user's teams
      const teamId = selectedTeam !== "all" ? selectedTeam : user.team_ids[0];
      const response = await axios.get(`${API}/teams/${teamId}/members`);
      setTeamMembers(response.data.filter(m => m.role !== "ADMIN"));
    } catch (error) {
      console.error("Failed to fetch team members");
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("from_date", selectedDate);
      params.append("to_date", selectedDate);
      if (selectedTeam !== "all") {
        params.append("team_id", selectedTeam);
      }
      if (selectedUser !== "all") {
        params.append("user_id", selectedUser);
      }

      const response = await axios.get(`${API}/team/logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const totalMembers = teamMembers.length;
  const submittedCount = logs.length;
  const uniqueUserIds = new Set(logs.map((l) => l.user_id));
  const missingCount = totalMembers - uniqueUserIds.size;

  // Get users who haven't submitted
  const missingUsers = teamMembers.filter((m) => !uniqueUserIds.has(m.id));

  if (!user?.team_ids || user.team_ids.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No Teams Assigned</h3>
            <p className="text-slate-500 mt-1">You are not assigned to any team yet.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Team Logs
        </h1>
        <p className="text-slate-600 mt-1">
          View logs from your teams: <span className="font-medium">{user.team_names?.join(", ")}</span>
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="w-[220px]">
          <Select value={selectedDate} onValueChange={setSelectedDate}>
            <SelectTrigger data-testid="team-logs-date-selector">
              <Calendar className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Select date" />
            </SelectTrigger>
            <SelectContent>
              {dateOptions.map((date) => (
                <SelectItem key={date.value} value={date.value}>
                  {date.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {teams.length > 1 && (
          <div className="w-[200px]">
            <Select value={selectedTeam} onValueChange={(val) => { setSelectedTeam(val); setSelectedUser("all"); }}>
              <SelectTrigger data-testid="team-logs-team-selector">
                <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filter by team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All My Teams</SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-[200px]">
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger data-testid="team-logs-user-selector">
              <User className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filter by user" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Team Members</SelectItem>
              {teamMembers.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalMembers}</p>
                <p className="text-sm text-slate-500">Team Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{submittedCount}</p>
                <p className="text-sm text-slate-500">Logs Submitted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{missingCount}</p>
                <p className="text-sm text-slate-500">Missing Logs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Missing Users */}
          {missingUsers.length > 0 && selectedUser === "all" && (
            <Card className="border-red-100">
              <CardHeader>
                <CardTitle className="text-red-700">Missing Logs</CardTitle>
                <CardDescription>Team members who haven't submitted their log for {format(parseISO(selectedDate), "MMMM d, yyyy")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {missingUsers.map((member) => (
                    <Badge key={member.id} variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      {member.full_name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submitted Logs */}
          {logs.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No logs found</h3>
                <p className="text-slate-500 mt-1">No logs match your filter criteria</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <Card key={log.id} data-testid={`team-log-${log.id}`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{log.user_name}</CardTitle>
                        <CardDescription>{log.user_email}</CardDescription>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Submitted
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Log Content */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                          Tasks Worked On Today
                        </h4>
                        <div 
                          className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: log.tasks_today }}
                        />
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                          Challenges and Hurdles
                        </h4>
                        <div 
                          className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: log.challenges }}
                        />
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                          Plan for Tomorrow
                        </h4>
                        <div 
                          className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: log.plan_tomorrow }}
                        />
                      </div>
                    </div>

                    {/* Metadata */}
                    <div className="pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-slate-500">
                          <Clock className="h-4 w-4" />
                          <span>
                            {formatTimeEST(log.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-500">
                          <Monitor className="h-4 w-4" />
                          <span>{log.browser_name} / {log.os_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-500">
                          <MapPin className="h-4 w-4" />
                          <span>
                            {[log.geo_city, log.geo_region, log.geo_country].filter(Boolean).join(", ") || log.ip_address}
                          </span>
                        </div>
                        <div className="text-slate-400 text-xs">
                          IP: {log.ip_address}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
